<template>
  <v-container fluid class="quotations-container">
    <!-- Header -->
    <v-card class="header-card mb-6" elevation="8">
      <v-card-text class="pa-8">
        <div class="d-flex justify-space-between align-center flex-wrap">
          <div>
            <h1 class="text-h4 text-white font-weight-bold mb-2">
              💍 Cotizaciones
            </h1>
            <p class="text-h6 text-white opacity-90">
              Gestión de cotizaciones - Wedding Planner
            </p>
          </div>
          <v-btn
            size="large"
            color="white"
            class="text-purple font-weight-bold mt-4 mt-md-0"
            prepend-icon="mdi-plus"
            elevation="4"
            @click="goToNew"
          >
            Nueva Cotización
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Estadísticas -->
    <v-row v-if="stats" class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card purple-stat" elevation="4">
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-body-2 text-grey-darken-1 mb-1">Total Cotizaciones</p>
                <p class="text-h3 font-weight-bold stat-value-purple">
                  {{ stats.totales.TotalCotizaciones || 0 }}
                </p>
              </div>
              <v-avatar size="64" color="purple-lighten-5">
                <v-icon size="40" color="purple">mdi-clipboard-list</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card green-stat" elevation="4">
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-body-2 text-grey-darken-1 mb-1">Monto Total</p>
                <p class="text-h6 font-weight-bold stat-value-green">
                  {{ formatCurrency(stats.totales.MontoTotal || 0) }}
                </p>
              </div>
              <v-avatar size="64" color="green-lighten-5">
                <v-icon size="40" color="green">mdi-cash-multiple</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card blue-stat" elevation="4">
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-body-2 text-grey-darken-1 mb-1">Total Abonado</p>
                <p class="text-h6 font-weight-bold stat-value-blue">
                  {{ formatCurrency(stats.totales.TotalAbonado || 0) }}
                </p>
              </div>
              <v-avatar size="64" color="blue-lighten-5">
                <v-icon size="40" color="blue">mdi-credit-card-check</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card orange-stat" elevation="4">
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-body-2 text-grey-darken-1 mb-1">Pendiente</p>
                <p class="text-h6 font-weight-bold stat-value-orange">
                  {{ formatCurrency(stats.totales.TotalPendiente || 0) }}
                </p>
              </div>
              <v-avatar size="64" color="orange-lighten-5">
                <v-icon size="40" color="orange">mdi-clock-alert</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filtros -->
    <v-card class="mb-6" elevation="4">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-filter</v-icon>
        Filtros
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.buscarNovios"
              label="Buscar por nombres de novios"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="comfortable"
              clearable
              @keyup.enter="loadQuotations"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="filters.estado"
              :items="estadoItems"
              label="Estado"
              prepend-inner-icon="mdi-format-list-bulleted"
              variant="outlined"
              density="comfortable"
              clearable
            ></v-select>
          </v-col>

          <v-col cols="12" md="2">
            <v-text-field
              v-model="filters.fechaDesde"
              label="Fecha desde"
              type="date"
              variant="outlined"
              density="comfortable"
              clearable
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="2">
            <v-text-field
              v-model="filters.fechaHasta"
              label="Fecha hasta"
              type="date"
              variant="outlined"
              density="comfortable"
              clearable
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="2" class="d-flex gap-2">
            <v-btn
              color="purple"
              variant="flat"
              block
              @click="loadQuotations"
            >
              Buscar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Loading -->
    <v-card v-if="loading" class="text-center pa-12" elevation="4">
      <v-progress-circular
        indeterminate
        color="purple"
        size="64"
        width="6"
      ></v-progress-circular>
      <p class="text-h6 mt-4">Cargando cotizaciones...</p>
    </v-card>

    <!-- Empty State -->
    <v-card v-else-if="quotations.length === 0" class="text-center pa-12" elevation="4">
      <v-icon size="120" color="grey-lighten-1">mdi-email-open-outline</v-icon>
      <p class="text-h5 mt-4 mb-2">No hay cotizaciones registradas</p>
      <p class="text-body-1 text-grey mb-6">Comienza creando tu primera cotización</p>
      <v-btn
        color="purple"
        size="large"
        variant="flat"
        prepend-icon="mdi-plus"
        @click="goToNew"
      >
        Crear Primera Cotización
      </v-btn>
    </v-card>

    <!-- Tabla de Cotizaciones -->
    <v-card v-else elevation="4">
      <v-data-table
        :headers="headers"
        :items="quotations"
        :items-per-page="10"
        class="elevation-0"
      >
        <!-- Número -->
        <template v-slot:[`item.NumeroCotizacion`]="{ item }">
          <span class="font-mono">
            {{ item.NumeroCotizacion }}
          </span>
        </template>

        <!-- Novios -->
        <template v-slot:[`item.NombreCliente`]="{ item }">
          <span class="font-weight-bold">{{ item.NombreCliente }}</span>
        </template>

        <!-- Tipo Evento -->
        <template v-slot:[`item.TipoEvento`]="{ item }">
          <span >{{ item.TipoEvento }}</span>
        </template>

        <!-- Fecha Evento -->
        <template v-slot:[`item.FechaEvento`]="{ item }">
          {{ formatDate(item.FechaEvento) }}
        </template>

        <!-- Total -->
        <template v-slot:[`item.TotalConDescuento`]="{ item }">
          <span class="text-green font-weight-bold">
            {{ formatCurrency(item.TotalConDescuento) }}
          </span>
        </template>

        <!-- Pendiente -->
        <template v-slot:[`item.SaldoPendiente`]="{ item }">
          <span class="text-orange font-weight-bold">
            {{ formatCurrency(item.SaldoPendiente) }}
          </span>
        </template>

        <!-- Total Abonado -->
        <template v-slot:[`item.TotalAbonosCotizacion`]="{ item }">
          <span class="text-green font-weight-bold">
            {{ formatCurrency(item.TotalAbonosCotizacion) }}
          </span>
        </template>

        <!-- Estado -->
        <template v-slot:[`item.Estado`]="{ item }">
          <v-chip
            :color="getStatusColor(item.Estado)"
            size="small"
            variant="flat"
          >
            {{ item.Estado }}
          </v-chip>
        </template>

        <!-- Estado Pago -->
        <template v-slot:[`item.EstadoPago`]="{ item }">
          <v-chip
            :color="getPaymentColor(item.EstadoPago)"
            size="small"
            variant="flat"
          >
            {{ item.EstadoPago }}
          </v-chip>
        </template>

        <!-- Acciones -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex gap-1">
            <v-btn
              icon="mdi-eye"
              size="small"
              color="blue"
              variant="tonal"
              @click="viewDetails(item.CotizacionID)"
            ></v-btn>
            
            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn
                  icon="mdi-dots-vertical"
                  size="small"
                  color="purple"
                  variant="tonal"
                  v-bind="props"
                ></v-btn>
              </template>
              <!-- <v-list>
                <v-list-item
                  v-for="estado in estadoItems"
                  :key="estado"
                  @click="updateStatus(item.cotizacionId, estado)"
                >
                  <v-list-item-title>{{ estado }}</v-list-item-title>
                </v-list-item>
              </v-list> -->
            <v-btn
              icon="mdi-file-pdf-box"
              size="small"
              color="blue"
              variant="flat"
              @click="getDataAndGeneratePDF(item.CotizacionID, false)"
            ></v-btn>
            <v-btn
              icon="mdi-delete"
              size="small"
              color="red"
              variant="flat"
              v-if="item.Estado != 'Confirmada'"
              @click="deleteQuotation(item)"
              :disabled="item.EstadoPago == 'Abonado'"
            ></v-btn>
            </v-menu>

                <!-- <v-btn
                  icon="mdi-currency-usd"
                  size="small"
                  color="purple"
                  variant="tonal"
                  title="Realizar un Abono"
                ></v-btn> -->

          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modal de Detalles -->
    <v-dialog v-model="showDetail" max-width="900" scrollable>
      <v-card v-if="selectedQuotation">
        <!-- Header del Modal -->
        <v-card-title class="modal-header-gradient text-white pa-6">
          <div class="d-flex justify-space-between align-center w-100">
            <div>
              <h2 class="text-h4 mb-2">{{ selectedQuotation.cotizacion.NombreCliente }}</h2>
              <p class="text-body-1 opacity-90">{{ selectedQuotation.cotizacion.NumeroCotizacion }}</p>
            </div>
            <v-btn
              icon="mdi-close"
              variant="text"
              color="white"
              @click="closeDetail"
            ></v-btn>
          </div>
        </v-card-title>

        <v-card-text class="pa-6">
          <!-- Info General -->
          <v-row class="mb-6">
            <v-col cols="12" sm="6" md="3">
              <v-card variant="tonal" >
                <v-card-text>
                  <p class="text-caption text-grey-darken-1 mb-1">Fecha del Evento</p>
                  <p class="text-body-1 font-weight-bold">
                    {{ formatDate(selectedQuotation.cotizacion.FechaEvento) }}
                  </p>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-card variant="tonal" >
                <v-card-text>
                  <p class="text-caption text-grey-darken-1 mb-1">Invitados</p>
                  <p class="text-body-1 font-weight-bold">
                    {{ selectedQuotation.cotizacion.NumeroInvitados }}
                  </p>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-card variant="tonal" >
                <v-card-text>
                  <p class="text-caption text-grey-darken-1 mb-1">Estado</p>
                  <v-chip
                    :color="getStatusColor(selectedQuotation.cotizacion.Estado)"
                    size="small"
                    variant="flat"
                  >
                    {{ selectedQuotation.cotizacion.Estado }}
                  </v-chip>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-card variant="tonal" >
                <v-card-text>
                  <p class="text-caption text-grey-darken-1 mb-1">Fecha de Creación</p>
                  <p class="text-body-1 font-weight-bold">
                    {{ formatDate(selectedQuotation.cotizacion.FechaCreacion) }}
                  </p>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Servicios -->
          <div v-if="selectedQuotation.servicios" class="mb-6">
            <h3 class="text-h6 mb-4">
              <v-icon class="mr-2">mdi-clipboard-list</v-icon>
              Servicios Contratados:
            </h3>
            <v-card
              v-for="(service, idx) in selectedQuotation.servicios"
              :key="idx"
              class="mb-3"
              variant="outlined"
              border="start"
              color="purple"
            >
              <v-card-text>
                <div class="d-flex justify-space-between align-center mb-3">
                  <h4 class="text-h6 text-purple">{{ service.NombreServicio }}</h4>
                  <span class="text-h6 font-weight-bold text-purple">
                    {{ formatCurrency(service.Precio) }}
                  </span>
                </div>
                <div v-for="seccion in service.secciones" :key="seccion.NombreSeccion" class="mb-2">
                  <p class="text-caption font-weight-bold text-grey-darken-2 mb-1 ml-1">
                    {{ seccion.NombreSeccion }}
                  </p>
                  <v-list density="compact" class="bg-transparent">
                    <v-list-item
                      v-for="(item, i) in seccion.items"
                      :key="i"
                      class="px-0"
                    >
                      <template v-slot:prepend>
                        <v-icon size="small" color="purple">mdi-circle-small</v-icon>
                      </template>
                      {{ item }}
                    </v-list-item>
                  </v-list>
                </div>
              </v-card-text>
            </v-card>
          </div>

          <!-- Resumen Financiero -->
          <v-card variant="tonal"  class="mb-6">
            <v-card-title>
              <v-icon class="mr-2">mdi-calculator</v-icon>
              Resumen Financiero
            </v-card-title>
            <v-card-text>
              <v-list density="compact" class="bg-transparent">
                <v-list-item>
                  <div class="d-flex justify-space-between w-100">
                    <span>Subtotal:</span>
                    <span class="font-weight-bold">
                      {{ formatCurrency(selectedQuotation.cotizacion.Subtotal) }}
                    </span>
                  </div>
                </v-list-item>

                <v-list-item class="text-red">
                  <div class="d-flex justify-space-between w-100">
                    <span>Descuento ({{ selectedQuotation.cotizacion.PorcentajeDescuento }}%):</span>
                    <span class="font-weight-bold">
                      -{{ formatCurrency(selectedQuotation.cotizacion.MontoDescuento) }}
                    </span>
                  </div>
                </v-list-item>

                <v-divider class="my-2"></v-divider>

                <v-list-item class="bg-purple-lighten-5">
                  <div class="d-flex justify-space-between w-100">
                    <span class="text-h6 font-weight-bold">Total:</span>
                    <span class="text-h6 font-weight-bold text-purple">
                      {{ formatCurrency(selectedQuotation.cotizacion.TotalConDescuento) }}
                    </span>
                  </div>
                </v-list-item>

                <v-list-item class="bg-green-lighten-5">
                  <div class="d-flex justify-space-between w-100">
                    <span class="font-weight-bold">Abonado:</span>
                    <span class="font-weight-bold text-green">
                      {{ formatCurrency(selectedQuotation.cotizacion.TotalAbonosCotizacion) }}
                      <!-- {{ formatCurrency(selectedQuotation.cotizacion.TotalAbonos) }} -->
                    </span>
                  </div>
                </v-list-item>

                <v-list-item class="bg-orange-lighten-5">
                  <div class="d-flex justify-space-between w-100">
                    <span class="text-h6 font-weight-bold">Pendiente:</span>
                    <span class="text-h6 font-weight-bold text-orange">
                      {{ formatCurrency(selectedQuotation.cotizacion.SaldoPendiente) }}
                    </span>
                  </div>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>

          <!-- Abonos -->
          <v-btn 
            size="large"
            color="purple"
            block
            variant="tonal"
            title="Realizar un Abono"
            class="mt-4 mb-4"
            @click="mostrarAbonos = !mostrarAbonos"
            :disabled="selectedQuotation.cotizacion.SaldoPendiente <= 0"
          >
          <span v-if="mostrarAbonos"><v-icon start>mdi-chevron-up</v-icon> Ocultar Abonos</span>
          <span v-else><v-icon start>mdi-chevron-down</v-icon> Agregar Abono</span>
          </v-btn>
              <!-- Columna Derecha - Abonos -->
              <v-col cols="12" md="12" v-if="mostrarAbonos && selectedQuotation.cotizacion.SaldoPendiente > 0">
                <v-card variant="outlined" class="bg-grey-lighten-5">
                  <v-card-title class="text-subtitle-1 pa-4">
                    <v-icon start>mdi-cash-check</v-icon>
                    Abonos
                  </v-card-title>
                  <v-card-text>
                    <v-row v-for="(payment, idx) in payments" :key="idx" dense>
                      <v-col cols="9">
                        <v-text-field
                          v-model.number="payment.amount"
                          label="Monto del abono"
                          type="number"
                          prefix="$"
                          variant="outlined"
                          density="compact"
                          :max="pendingAmount"
                          hide-details
                        />
                      </v-col>
                      <v-col cols="3">
                        <v-btn
                          v-if="payments.length > 1"
                          icon
                          color="error"
                          variant="text"
                          @click="removePayment(idx)"
                        >
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>

                    <v-btn
                      block
                      color="primary"
                      variant="outlined"
                      prepend-icon="mdi-plus"
                      class="mt-3"
                      @click="addPayment"
                    >
                      Otro Abono
                    </v-btn>

                    <v-divider class="my-4" />

                    <v-list-item class="bg-warning-lighten-4 rounded">
                      <template v-slot:prepend>
                        <v-icon color="warning">mdi-alert-circle</v-icon>
                      </template>
                      <v-list-item-title class="font-weight-bold">Pendiente :</v-list-item-title>
                      <template v-slot:append>
                        <span class="font-weight-bold text-h6" :class="pendingAmount < 0 ? 'stat-value-red' : 'text-warning'">{{ formatCurrency(pendingAmount) }}</span>
                      </template>
                    </v-list-item>
                    <v-btn
                      block
                      color="primary"
                      variant="flat"
                      prepend-icon="mdi-cash-check"
                      class="mt-3"
                      :disabled="pendingAmount < 0"
                      @click="savePayment"
                    >
                      Realizar Abono
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>
              <div v-if="selectedQuotation.abonos?.length" class="mb-6">
                <h3 class="text-h6 mb-4">
                  <v-icon class="mr-2">mdi-credit-card</v-icon>
                  Abonos Registrados
                </h3>
                <v-row>
                  <v-col
                    v-for="(abono, idx) in selectedQuotation.abonos"
                    :key="idx"
                    cols="12"
                    sm="6"
                    md="4"
                  >
                    <v-card variant="outlined" border="start" color="green">
                      <v-card-text>
                        <div class="d-flex justify-space-between align-center">
                          <div>
                            <p class="text-caption text-grey-darken-1">
                              Abono {{ abono.NumeroAbono }}
                            </p>
                            <p class="text-h6 font-weight-bold text-green">
                              {{ formatCurrency(abono.Monto) }}
                            </p>
                            <p class="text-caption text-grey-darken-1">
                              {{ formatDate(abono.FechaAbono) }}
                            </p>
                          </div>
                          <v-icon size="40" color="green-lighten-1">
                            mdi-check-circle
                          </v-icon>
                          <v-btn
                            v-if="pendingAmount > 0"
                            icon
                            color="error"
                            variant="text"
                            :disabled="idx !== selectedQuotation.abonos.length - 1"
                            @click="removeAbono(abono)"
                          >
                            <v-icon>mdi-trash-can-outline</v-icon>
                          </v-btn>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

          <!-- Observaciones -->
          <v-card
            v-if="selectedQuotation.cotizacion.Observaciones"
            variant="outlined"
            border="start"
            color="orange"
          >
            <v-card-title>
              <v-icon class="mr-2">mdi-note-text</v-icon>
              Observaciones
            </v-card-title>
            <v-card-text>
              <p class="text-body-1">{{ selectedQuotation.cotizacion.Observaciones }}</p>
            </v-card-text>
          </v-card>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-btn
            color="primary"
            variant="flat"
            prepend-icon="mdi-file-pdf-box"
            @click="getDataAndGeneratePDF(selectedQuotation.cotizacion.CotizacionID, true)"
          >
            Generar PDF
          </v-btn>
          <!-- <v-spacer></v-spacer> -->
          <!-- <v-btn
            color="error"
            variant="flat"
            @click="closeDetail"
          >
            Cerrar
          </v-btn> -->
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="toast.show"
      :color="toast.type === 'success' ? 'green' : 'red'"
      :timeout="3000"
      location="top right"
    >
      {{ toast.message }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="toast.show = false"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>

<!-- Diálogo de recarga -->
<v-dialog v-model="reloadDialog" max-width="420" persistent>
  <v-card>
    <v-card-title class="text-h6 pt-5 px-6">
      <v-icon color="primary" class="mr-2">mdi-refresh</v-icon>
      Actualizar cotizaciones
    </v-card-title>
    <v-card-text class="px-6">
      Acabas de crear una nueva cotización. ¿Deseas recargar la lista para ver las cotizaciones actualizadas?
    </v-card-text>
    <v-card-actions class="pa-4">
      <v-spacer />
      <v-btn variant="text" @click="reloadDialog = false">No, mantener</v-btn>
      <v-btn color="primary" variant="elevated" @click="confirmReload">Sí, actualizar</v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onActivated } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { generatePDF as generatePDFUtil } from '@/utils/pdfGenerator'

defineOptions({
  name: 'cotizacionList'
})

const router = useRouter()
const store = useStore()

const route = useRoute()  // agregar junto a router y store

const reloadDialog = ref(false)

const CotizacionesList = computed(() => store.state.Cotizacion.CotizacionesList)
const Cotizacion = computed(() => store.state.Cotizacion.Cotizacion)
const totalItems = computed(() => store.state.Cotizacion.totalItems)
const page = computed(() => store.state.Cotizacion.page)
const limit = computed(() => store.state.Cotizacion.limit)

const saving = ref(false)

const catalogoServicios = ref<any[]>([])
const catalogoItems = ref<any[]>([])

// Reactive state
const quotations = ref<any[]>([])
const stats = ref<any>(null)
const loading = ref(true)
const showDetail = ref(false)
const mostrarAbonos = ref(false)
const selectedQuotation = ref<any>(null)
const filters = ref({
  estado: '',
  buscarNovios: '',
  fechaDesde: '',
  fechaHasta: ''
})
const toast = ref({
  show: false,
  message: '',
  type: 'success'
})

const payments = ref([{ amount: 0 }])

// Constants
// const apiUrl = 'http://localhost:3000/api'
const estadoItems = ['Pendiente', 'Aprobada', 'Rechazada', 'Finalizada']
const headers: any[] = [
  { title: 'Número', key: 'NumeroCotizacion', align: 'start' },
  { title: 'Cliente', key: 'NombreCliente', align: 'start' },
  { title: 'Tipo Evento', key: 'TipoEvento', align: 'start' },
  { title: 'Fecha Evento', key: 'FechaEvento', align: 'start' },
  { title: 'Invitados', key: 'NumeroInvitados', align: 'center' },
  { title: 'Total', key: 'TotalConDescuento', align: 'end' },
  { title: 'Pendiente', key: 'SaldoPendiente', align: 'end' },
  { title: 'Abonado', key: 'TotalAbonosCotizacion', align: 'end' },
  { title: 'Estado', key: 'Estado', align: 'center' },
  { title: 'Estado Pago', key: 'EstadoPago', align: 'center' },
  { title: 'Acciones', key: 'actions', align: 'center', sortable: false }
]
function removePayment(index: number) {
  if (payments.value.length > 1) {
    payments.value.splice(index, 1)
  }
}
function addPayment() {
  payments.value.push({ amount: 0 })
}
async function savePayment() { 
  const cotizacionID = Cotizacion.value.data.data.cotizacion.CotizacionID
    const abonos = payments.value.map(payment => {
      if (payment.amount > 0) {
        return {
          monto: payment.amount,
          metodoPago: 'Efectivo',
          referencia: generateReference('REF'),
          observaciones: '',
        }
      }
    }).filter(item => item !== undefined)
    await store.dispatch('Cotizacion/agregarAbono', { abonos, cotizacionID })
    // showDetail.value = false
    viewDetails(cotizacionID)
    payments.value = [{ amount: 0 }]
    loadQuotations()
}
async function removeAbono(abono: any) {
  if (!confirm('¿Estás seguro de eliminar este abono?')) return
  await store.dispatch('Cotizacion/eliminarAbono', abono)
  const cotizacionID = Cotizacion.value.data.data.cotizacion.CotizacionID
  viewDetails(cotizacionID)
  loadQuotations()
}
function generateReference(prefijo: string) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let reference = prefijo+'-'
  for (let i = 0; i < 10; i++) {
    reference += characters.charAt(Math.floor(Math.random() * characters.length))
  }
  return reference
}

const totalWithDiscount = computed(() => {
  return selectedQuotation.value.cotizacion.SaldoPendiente
})

const totalPaid = computed(() => {
  return payments.value.reduce((sum, p) => sum + (p.amount || 0), 0)
})

const pendingAmount = computed(() => {
  return totalWithDiscount.value - totalPaid.value
})

async function loadQuotations() {
  loading.value = true
  try {
    const params = new URLSearchParams()
    if (filters.value.estado) params.append('estado', filters.value.estado)
    if (filters.value.buscarNovios) params.append('buscarNovios', filters.value.buscarNovios)
    if (filters.value.fechaDesde) params.append('fechaDesde', filters.value.fechaDesde)
    if (filters.value.fechaHasta) params.append('fechaHasta', filters.value.fechaHasta)

    await store.dispatch('Cotizacion/fetchCotizaciones',{})

    quotations.value = CotizacionesList.value.data.data
  } catch (error) {
    console.error('Error cargando cotizaciones:', error)
    showToast('Error de conexión con el servidor', 'error')
  } finally {
    loading.value = false
  }
}

async function viewDetails(id: number, showModal = true) {
  try {
    await store.dispatch('Cotizacion/fetchObtenerCotizacion', id)
    selectedQuotation.value = Cotizacion.value.data.data
    if (showModal) {
      showDetail.value = true
    }
  } catch (error) {
    console.error('Error cargando detalles:', error)
    showToast('Error de conexión', 'error')
  }
}

async function deleteQuotation(cotizacion: any) {
  if (!confirm('¿Estás seguro de eliminar esta cotización?')) return
  
  try {
    await store.dispatch('Cotizacion/eliminarCotizacion', cotizacion)
    showToast('Cotización eliminada exitosamente')
    loadQuotations()
  } catch (error) {
    console.error('Error eliminando cotización:', error)
    showToast('Error de conexión', 'error')
  }
}

async function updateStatus(id: number, newStatus: string) {
  if (!newStatus) return
  
  try {
    showToast('Estado actualizado correctamente')
    loadQuotations()
  } catch (error) {
    console.error('Error actualizando estado:', error)
    showToast('Error de conexión', 'error')
  }
}

function clearFilters() {
  filters.value = {
    estado: '',
    buscarNovios: '',
    fechaDesde: '',
    fechaHasta: ''
  }
  loadQuotations()
}

function closeDetail() {
  showDetail.value = false
  selectedQuotation.value = null
}

function goToNew() {
  router.push('/cotizaciones/new')
}

async function getDataAndGeneratePDF(cotizacionId: number, showModal = true) {
  await viewDetails(cotizacionId, showModal)
  await generatePDF()
}

async function generatePDF() {
  saving.value = true
  try {
    if (selectedQuotation.value.cotizacion.PdfData) {
    const pdfData = JSON.parse(selectedQuotation.value.cotizacion.PdfData);
      await generatePDFUtil(pdfData)
      showToast('✅ PDF generado exitosamente')
    }else{
      showToast('No se pudo generar el PDF', 'error')
    } 
  } catch (error) {
    console.error('Error generando PDF:', error)
    showToast('Error al generar el PDF', 'error')
  } finally {
    saving.value = false
    // selectedItems.value = []
  }
}

function showToast(message: string, type = 'success') {
  toast.value = { show: true, message, type }
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0
  }).format(value || 0)
}

function formatDate(dateString: string) {
  if (!dateString) return '-'
  
  // Extraer año, mes y día del string para evitar problemas de zona horaria
  const [year, month, day] = dateString.split('T')[0].split('-')
  
  // Crear fecha en zona horaria local
  const date = new Date(Number.parseInt(year), Number.parseInt(month) - 1, Number.parseInt(day))
  
  return date.toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

function getStatusColor(estado: string) {
  const colors: Record<string, string> = {
    'Pendiente': 'warning',
    'Aprobada': 'success',
    'Rechazada': 'error',
    'Finalizada': 'info'
  }
  return colors[estado] || 'default'
}

function getPaymentColor(estadoPago: string) {
  const colors: Record<string, string> = {
    'Pagado': 'success',
    'Abonado': 'info',
    'Sin Abonos': 'error'
  }
  return colors[estadoPago] || 'default'
}

// Lifecycle
onMounted(() => {
  loadQuotations()
})

onActivated(() => {
  // Si viene desde cotizacionNew, preguntar si quiere recargar
  debugger
  if (route.query.from === 'opciones') {
    reloadDialog.value = true
    // Limpiar el query para que no vuelva a dispararse si navega entre tabs
    router.replace({ query: {} })
  }
})
function confirmReload() {
  reloadDialog.value = false
  loadQuotations()
}
</script>

<style scoped>
.quotations-container {
  background: linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%);
  min-height: 100vh;
}

.header-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

.text-purple {
  color: #667eea !important;
}

/* Stat Cards */
.stat-card {
  transition: transform 0.3s ease;
  border-radius: 12px !important;
}

.stat-card:hover {
  transform: translateY(-5px);
}

.stat-value-purple {
  color: #667eea;
}

.stat-value-green {
  color: #27ae60;
}

.stat-value-blue {
  color: #3498db;
}

.stat-value-orange {
  color: #e67e22;
}

.stat-value-red {
  color: #e74c3c;
}

/* Font utilities */
.font-mono {
  font-family: 'Courier New', monospace;
}

.opacity-90 {
  opacity: 0.9;
}

/* Modal Header */
.modal-header-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

/* Colors for amounts */
.text-green {
  color: #27ae60 !important;
}

.text-orange {
  color: #e67e22 !important;
}

.text-red {
  color: #e74c3c !important;
}

/* Gap utility */
.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .header-card h1 {
    font-size: 2rem !important;
  }
  
  .header-card .text-h6 {
    font-size: 1rem !important;
  }
}

@media (max-width: 600px) {
  .header-card h1 {
    font-size: 1.5rem !important;
  }
}

v-data-table {
    font-size: 12px;
    font-family: math;
}
</style>
